import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';

type EmailTemplateItem = {
  id: number;
  organizationId: number;
  templateKey: string;
  name: string;
  subjectTemplate: string;
  bodyHtmlTemplate: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

type FormState = {
  templateKey: string;
  name: string;
  subjectTemplate: string;
  bodyHtmlTemplate: string;
  description: string;
  isActive: boolean;
};

type ApiErrorPayload = {
  message?: string;
};

const emptyForm: FormState = {
  templateKey: '',
  name: '',
  subjectTemplate: '',
  bodyHtmlTemplate: '',
  description: '',
  isActive: true,
};

const normalizeTemplateKey = (value: string): string =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]+/g, '_');

const extractApiErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const payload = err.response?.data as ApiErrorPayload | undefined;
    if (payload?.message && payload.message.trim()) {
      return payload.message;
    }
  }

  if (err instanceof Error && err.message.trim()) {
    return err.message;
  }

  return fallback;
};

const ManageEmailTemplates: React.FC = () => {
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const API_BASE = `${config.apiBaseUrl}/api`;

  const [items, setItems] = useState<EmailTemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);

  const filteredItems = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return items.filter((item) => {
      const matchesActive =
        activeFilter === 'all' ||
        (activeFilter === 'active' && item.isActive) ||
        (activeFilter === 'inactive' && !item.isActive);

      if (!matchesActive) {
        return false;
      }

      if (!query) {
        return true;
      }

      return (
        item.templateKey.toLowerCase().includes(query) ||
        item.name.toLowerCase().includes(query) ||
        item.subjectTemplate.toLowerCase().includes(query) ||
        (item.description || '').toLowerCase().includes(query)
      );
    });
  }, [activeFilter, items, searchText]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setMessage(null);

      if (!hasActiveOrganization) {
        setItems([]);
        return;
      }

      const token = await getManagementToken();
      const response = await axios.get<EmailTemplateItem[]>(`${API_BASE}/emailtemplates`, {
        headers: getOrganizationHeaders(token),
      });

      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to load email templates'));
    } finally {
      setLoading(false);
    }
  }, [API_BASE, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const startAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setMessage(null);
  };

  const startEdit = (item: EmailTemplateItem) => {
    setIsAdding(false);
    setEditingId(item.id);
    setForm({
      templateKey: item.templateKey,
      name: item.name,
      subjectTemplate: item.subjectTemplate,
      bodyHtmlTemplate: item.bodyHtmlTemplate,
      description: item.description || '',
      isActive: item.isActive,
    });
    setError(null);
    setMessage(null);
  };

  const cancelEdit = () => {
    setIsAdding(false);
    setEditingId(null);
    setIsPreviewOpen(false);
    setForm(emptyForm);
  };

  const saveItem = async () => {
    if (!hasActiveOrganization) {
      setError('Please select an organization before managing email templates.');
      return;
    }

    const normalizedTemplateKey = normalizeTemplateKey(form.templateKey);
    const name = form.name.trim();
    const subjectTemplate = form.subjectTemplate.trim();
    const bodyHtmlTemplate = form.bodyHtmlTemplate.trim();

    if (!normalizedTemplateKey && isAdding) {
      setError('Template key is required');
      return;
    }

    if (!name) {
      setError('Name is required');
      return;
    }

    if (!subjectTemplate) {
      setError('Subject template is required');
      return;
    }

    if (!bodyHtmlTemplate) {
      setError('Body HTML template is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setMessage(null);

      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);

      if (isAdding) {
        await axios.post(
          `${API_BASE}/emailtemplates`,
          {
            templateKey: normalizedTemplateKey,
            name,
            subjectTemplate,
            bodyHtmlTemplate,
            description: form.description.trim() || null,
            isActive: form.isActive,
          },
          { headers }
        );
      } else if (editingId) {
        await axios.put(
          `${API_BASE}/emailtemplates/${editingId}`,
          {
            name,
            subjectTemplate,
            bodyHtmlTemplate,
            description: form.description.trim() || null,
            isActive: form.isActive,
          },
          { headers }
        );
      }

      await loadData();
      cancelEdit();
      setMessage('Email template saved successfully.');
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to save email template'));
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id: number) => {
    if (!window.confirm('Delete this email template?')) {
      return;
    }

    try {
      setError(null);
      setMessage(null);
      const token = await getManagementToken();

      await axios.delete(`${API_BASE}/emailtemplates/${id}`, {
        headers: getOrganizationHeaders(token),
      });

      setItems((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) {
        cancelEdit();
      }

      setMessage('Email template deleted successfully.');
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to delete email template'));
    }
  };

  return (
    <div className="bg-gradient-to-br from-app-page-from to-app-page-to" style={{ width: '100%', minHeight: '100vh' }}>
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => window.location.assign('/admin')} className="app-breadcrumb-link">Admin</button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">Manage Email Templates</span>
        </div>
      </div>

      <div className="p-3 md:p-6">
        {!hasActiveOrganization && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 text-left">
            Select an organization first to manage email templates.
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 text-left">{error}</div>
        )}

        {message && (
          <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 text-left">{message}</div>
        )}

        <div className="rounded-lg bg-white shadow-md overflow-hidden">
          <div className="p-4 md:p-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between gap-3">
              <div className="text-left">
                <h1 className="text-xl md:text-2xl font-bold">Manage Email Templates</h1>
                <p className="text-xs md:text-sm text-white/80">Organization-scoped HTML templates and subjects for transactional emails</p>
              </div>
              <button
                onClick={startAdd}
                className="rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30"
                disabled={!hasActiveOrganization}
              >
                + Add Template
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr]">
            <div className="border-r border-slate-100 p-4">
              <div className="grid grid-cols-1 gap-2 mb-3">
                <input
                  type="text"
                  placeholder="Search by key, name, subject"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="all">All templates</option>
                  <option value="active">Active only</option>
                  <option value="inactive">Inactive only</option>
                </select>
              </div>

              <div className="space-y-2 max-h-[65vh] overflow-auto pr-1">
                {loading ? (
                  <div className="text-sm text-slate-500 text-left">Loading templates...</div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-sm text-slate-500 text-left">No templates found.</div>
                ) : (
                  filteredItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => startEdit(item)}
                      className={`w-full rounded-lg border p-3 text-left transition ${
                        editingId === item.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold tracking-wide text-slate-700">{item.templateKey}</span>
                        <span className={`text-[11px] font-semibold px-2 py-1 rounded ${item.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-800">{item.name}</div>
                      <div className="mt-1 text-xs text-slate-600 line-clamp-2">{item.subjectTemplate}</div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="p-4 md:p-5 text-left">
              {!isAdding && !editingId ? (
                <div className="text-sm text-slate-600">Select a template to edit, or create a new template.</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {isAdding && (
                    <label className="text-sm text-slate-700 font-semibold">
                      Template Key
                      <input
                        type="text"
                        value={form.templateKey}
                        onChange={(e) => setForm((prev) => ({ ...prev, templateKey: normalizeTemplateKey(e.target.value) }))}
                        placeholder="PAYMENT_SUCCESS"
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </label>
                  )}

                  {!isAdding && (
                    <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                      Template key cannot be changed after creation.
                    </div>
                  )}

                  <label className="text-sm text-slate-700 font-semibold">
                    Name
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>

                  <label className="text-sm text-slate-700 font-semibold">
                    Subject Template
                    <input
                      type="text"
                      value={form.subjectTemplate}
                      onChange={(e) => setForm((prev) => ({ ...prev, subjectTemplate: e.target.value }))}
                      placeholder="ExamMasters - Payment Confirmed for {{UserName}}"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>

                  <label className="text-sm text-slate-700 font-semibold">
                    Body HTML Template
                    <textarea
                      value={form.bodyHtmlTemplate}
                      onChange={(e) => setForm((prev) => ({ ...prev, bodyHtmlTemplate: e.target.value }))}
                      rows={14}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                    />
                  </label>

                  <div>
                    <button
                      type="button"
                      className="rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                      onClick={() => setIsPreviewOpen(true)}
                      disabled={!form.bodyHtmlTemplate.trim()}
                    >
                      Preview Email HTML
                    </button>
                  </div>

                  <label className="text-sm text-slate-700 font-semibold">
                    Description
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>

                  <label className="inline-flex items-center gap-2 text-sm text-slate-700 font-semibold">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    />
                    Active template
                  </label>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                    Placeholders use double braces, for example: {'{{UserName}}'} and {'{{PlanName}}'}.
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                      onClick={saveItem}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Template'}
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={cancelEdit}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    {!isAdding && editingId && (
                      <button
                        type="button"
                        className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                        onClick={() => void deleteItem(editingId)}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isPreviewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsPreviewOpen(false)}
        >
          <div
            className="w-full max-w-5xl rounded-xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div className="text-left">
                <h2 className="text-base font-bold text-slate-800">Email Preview</h2>
                <p className="text-xs text-slate-500">Rendered HTML output from Body HTML Template</p>
              </div>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => setIsPreviewOpen(false)}
              >
                Close
              </button>
            </div>

            <div className="border-b border-slate-200 px-4 py-3 text-sm text-slate-700 text-left">
              <div className="font-semibold">Subject Preview</div>
              <div>{form.subjectTemplate || '(empty subject)'}</div>
            </div>

            <iframe
              title="Email HTML Preview"
              srcDoc={form.bodyHtmlTemplate}
              className="w-full"
              style={{ height: '70vh', border: 'none', background: '#fff' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEmailTemplates;
